import React, { useEffect, useMemo } from 'react'
import { actionAtom } from 'atoms/atoms'
import { useAtom, useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { Box, Button, Box as FormActions, Box as FormFields } from '@node-space/ui-components'
import { TWO_FACTOR_CODE_LENGTH } from 'constants/General'
import { contactAtom } from 'pages/Contacts/atoms/contactAtoms'
import { useFiatInstrumentForm } from 'pages/Contacts/hooks/useFiatInstrumentForm'
import { ContactFiatInstrumentFormKeys } from 'pages/Contacts/schemas/contactFiatInstrumentSchema'
import { Steps } from 'pages/Contacts/types/fiatInstruments'
import { getFieldsToValidate } from 'pages/Contacts/utils/fiatInstruments'
import { BeneficiaryTransferDestination } from 'types/beneficiaries'
import { ContactCard } from '../ContactCard'
import { BankAccountDetails } from './BankAccountDetails'
import { FiatInstrumentConfirmation } from './FiatInstrumentConfirmation'
import { IntermediaryBankDetails } from './IntermediaryBankDetails'
import { TransferDestinationSelector } from './TransferDestinationSelector'

export const ContactFiatInstrumentForm = () => {
  const { t } = useTranslation()

  const contact = useAtomValue(contactAtom)
  const [action, setAction] = useAtom(actionAtom)

  const {
    errors,
    formFieldLabels,
    formValues,
    isBusinessBankAccount,
    isInternationalBankAccount,
    isSubmittingBankAccount,
    step,
    isSubmitting2Fa,
    clearErrors,
    handleSubmit,
    onSubmit,
    register,
    setStep,
    setValue,
    submit2Fa,
    trigger,
  } = useFiatInstrumentForm()

  useEffect(() => {
    const { twoFactorCode } = formValues || {}
    if (twoFactorCode?.length === TWO_FACTOR_CODE_LENGTH) {
      submit2Fa({
        challenge: formValues?.challengeCode,
        tokenResponse: formValues?.twoFactorCode,
        callbackUrl: formValues?.callbackUrl,
      })
    }
  }, [formValues?.twoFactorCode])

  const handleTransferDestinationSelect = (transferDestination: BeneficiaryTransferDestination) => {
    setValue('transferDestination', transferDestination)
    const isInternational = transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
    if (isInternational) {
      setStep(Steps.INTERNATIONAL_BANK_DETAILS)
    } else {
      setStep(Steps.LOCAL_BANK_DETAILS)
    }
  }

  const fieldsToValidate: ContactFiatInstrumentFormKeys[] = useMemo(() => {
    return getFieldsToValidate(step)
  }, [step])

  const handleContinue = async () => {
    const isValid = await trigger(fieldsToValidate)

    if (isValid && step === Steps.INTERNATIONAL_BANK_DETAILS) {
      setStep(Steps.INTERMEDIARY_BANK_DETAILS)
    }
  }

  const onContinue = async () => {
    const isValid = await trigger(fieldsToValidate)

    if (isValid) {
      setStep(Steps.CONFIRMATION)
    }
  }

  const actions = useMemo(() => {
    switch (step) {
      case Steps.LOCAL_BANK_DETAILS:
        return (
          <FormActions flex justify="between" paddingT={20}>
            <Button
              testId="cancel"
              type="button"
              variant="secondary"
              onClick={() => {
                action?.onClose()
              }}
            >
              {t('cancel')}
            </Button>
            <Button
              testId="add-fiat-instrument"
              type="submit"
              variant="primary"
              loading={isSubmittingBankAccount}
              onClick={onContinue}
            >
              {t('contacts.addBankAccount')}
            </Button>
          </FormActions>
        )
      case Steps.INTERNATIONAL_BANK_DETAILS:
        return (
          <FormActions flex justify="between" paddingT={20}>
            <Button
              testId="cancel"
              type="button"
              variant="secondary"
              onClick={() => {
                action?.onClose()
              }}
            >
              {t('cancel')}
            </Button>
            <Button testId="add-int-fiat-instrument" variant="primary" onClick={handleContinue}>
              {t('continue')}
            </Button>
          </FormActions>
        )
      case Steps.INTERMEDIARY_BANK_DETAILS:
        return (
          <FormActions flex justify="between" paddingT={20}>
            <Button testId="skip-this-step" type="submit" variant="secondary" onClick={onContinue}>
              {t('skipThisStep')}
            </Button>
            <Button testId="add-int-fiat-instrument" variant="primary" onClick={onContinue}>
              {t('contacts.addBankAccount')}
            </Button>
          </FormActions>
        )
      case Steps.CONFIRMATION:
      case Steps.SUCCESS:
      default:
        return
    }
  }, [step, isSubmittingBankAccount])

  const handleGoBack = () => {
    if (!isInternationalBankAccount) {
      if (step === Steps.LOCAL_BANK_DETAILS) {
        setStep(Steps.TRANSFER_TYPE)
      } else if (step === Steps.CONFIRMATION) {
        setStep(Steps.LOCAL_BANK_DETAILS)
      }
    }

    if (isInternationalBankAccount) {
      if (step === Steps.INTERNATIONAL_BANK_DETAILS) {
        setStep(Steps.TRANSFER_TYPE)
      } else if (step === Steps.INTERMEDIARY_BANK_DETAILS) {
        setStep(Steps.INTERNATIONAL_BANK_DETAILS)
      } else if (step === Steps.CONFIRMATION) {
        setStep(Steps.INTERMEDIARY_BANK_DETAILS)
      }
    }
  }

  const canGoBack = useMemo(() => {
    return (
      step === Steps.LOCAL_BANK_DETAILS ||
      step === Steps.CONFIRMATION ||
      step === Steps.INTERNATIONAL_BANK_DETAILS ||
      step === Steps.INTERMEDIARY_BANK_DETAILS
    )
  }, [step])

  useEffect(() => {
    setAction({
      ...action,
      ...(step === Steps.CONFIRMATION && { header: t('contacts.confirmBankAccountDetails') }),
      ...(step === Steps.INTERMEDIARY_BANK_DETAILS && {
        header: t('intermediaryBankDetails'),
      }),
      onBack: canGoBack ? handleGoBack : null,
    })
  }, [step, canGoBack])

  return (
    <Box width="full">
      <form data-testid="contact-bank-account-form" onSubmit={handleSubmit(onSubmit)}>
        <FormFields width="full" flexCol gapY={16} paddingT={24}>
          {step === Steps.TRANSFER_TYPE && (
            <TransferDestinationSelector
              onTransferDestinationClick={handleTransferDestinationSelect}
            />
          )}

          {step === Steps.LOCAL_BANK_DETAILS && (
            <Box paddingB={12}>
              <ContactCard name={contact?.name} />
              <BankAccountDetails
                formFieldLabels={formFieldLabels}
                formValues={formValues}
                isBusinessAccount={isBusinessBankAccount}
                isInternationalBankAccount={isInternationalBankAccount}
                setValue={setValue}
                clearErrors={clearErrors}
                register={register}
                errors={errors}
              />
            </Box>
          )}

          {step === Steps.INTERNATIONAL_BANK_DETAILS && (
            <Box paddingB={8}>
              <ContactCard name={contact?.name} />
              <BankAccountDetails
                formFieldLabels={formFieldLabels}
                formValues={formValues}
                isBusinessAccount={isBusinessBankAccount}
                isInternationalBankAccount={isInternationalBankAccount}
                setValue={setValue}
                clearErrors={clearErrors}
                register={register}
                errors={errors}
              />
            </Box>
          )}

          {step === Steps.INTERMEDIARY_BANK_DETAILS && (
            <Box>
              <ContactCard name={contact?.name} />
              <IntermediaryBankDetails
                formValues={formValues}
                setValue={setValue}
                clearErrors={clearErrors}
                register={register}
                errors={errors}
              />
            </Box>
          )}

          {step === Steps.CONFIRMATION && (
            <FiatInstrumentConfirmation
              formValues={formValues}
              formFieldLabels={formFieldLabels}
              setValue={setValue}
              isLoading={isSubmitting2Fa}
            />
          )}
        </FormFields>

        {actions}
      </form>
    </Box>
  )
}
